<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import {
	MercariProductTypeEnum,
	type IMercariProductListItem,
} from '@/types/Mercari'
import {
	getMercariUrl,
	getFormattedPrice,
	getMercariStatus,
} from '@/lib/mercari'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

enum TagDisplayState {
	NONE = 'none',
	SOLD_OUT = 'sold_out',
	DISCOUNT = 'discount',
	SALE = 'sale',
	SAVED_PERCENTAGE = 'saved_percentage',
	DISCOUNT_NOT_AVAILABLE = 'discount_not_available',
}

export interface IMercariProductListItemContent {
	discountText: string
	saved_percentage_label: string
	not_sold_locally_label: string
	saleText: string
	soldOutText: string
}

const props = defineProps({
	item: {
		type: Object as PropType<IMercariProductListItem>,
		default: () => ({}),
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	itemContent: {
		type: Object as PropType<IMercariProductListItemContent>,
		default: () => ({
			discountText: '{DISCOUNT_PERCENTAGE}% OFF',
			saved_percentage_label: 'SAVE {DISCOUNT_PERCENTAGE}%',
			not_sold_locally_label: 'Not Sold Locally',
			saleText: 'SALE',
			soldOutText: 'SOLDOUT',
		}),
	},
})

const emit = defineEmits(['item-click'])

const isImageLoaded = ref(false)
const targetUrl = computed(() => {
	if (props.item.isBuyaholicProduct) {
		return `${getSiteBaseUrl()}/sharing/posts/${props.item.id}`
	}
	return getMercariUrl('detail', { id: props.item.id })
})
const currentType = computed(() => {
	const { status, discountPercentage, hasPromotions } = props.item

	const mercariProductSaleType = getMercariStatus({
		isBuyaholicProduct: props.item.isBuyaholicProduct ?? false,
		status,
		discountPercentage,
		hasPromotions,
	})

	return mercariProductSaleType
})
const tagState = computed((): TagDisplayState => {
	if (
		!isImageLoaded.value ||
		currentType.value === MercariProductTypeEnum.NORMAL
	) {
		return TagDisplayState.NONE
	}

	if (props.item.isBuyaholicProduct) {
		switch (currentType.value as MercariProductTypeEnum) {
			case MercariProductTypeEnum.DISCOUNT_NOT_AVAILABLE:
				return TagDisplayState.DISCOUNT_NOT_AVAILABLE
			case MercariProductTypeEnum.SAVED_PERCENTAGE:
				return TagDisplayState.SAVED_PERCENTAGE
			case MercariProductTypeEnum.NORMAL:
				return TagDisplayState.NONE
			default:
				return TagDisplayState.NONE
		}
	}

	switch (currentType.value as MercariProductTypeEnum) {
		case MercariProductTypeEnum.SOLD_OUT:
			return TagDisplayState.SOLD_OUT
		case MercariProductTypeEnum.DISCOUNT:
			return TagDisplayState.DISCOUNT
		case MercariProductTypeEnum.SALE:
			return TagDisplayState.SALE
		default:
			return TagDisplayState.NONE
	}
})
const tagText = computed(() => {
	switch (tagState.value) {
		case TagDisplayState.DISCOUNT_NOT_AVAILABLE:
			return props.itemContent.not_sold_locally_label
		case TagDisplayState.SAVED_PERCENTAGE:
			// api response will be "10%", it needs replace to "10"
			return props.itemContent.saved_percentage_label.replace(
				'{DISCOUNT_PERCENTAGE}%',
				String(props.item.discountPercentage ?? ''),
			)
		case TagDisplayState.DISCOUNT:
			return props.itemContent.discountText.replace(
				'{DISCOUNT_PERCENTAGE}',
				String(props.item.discountPercentage ?? ''),
			)
		case TagDisplayState.SALE:
			return props.itemContent.saleText
		case TagDisplayState.SOLD_OUT:
			return props.itemContent.soldOutText
		default:
			return ''
	}
})
const isShowTag = computed(() => tagState.value !== TagDisplayState.NONE)

const tagClasses = computed(() => {
	const baseClasses =
		'typography-c1-reg absolute bottom-0 p-8px text-white text-center'

	switch (tagState.value) {
		case TagDisplayState.SOLD_OUT:
			return `${baseClasses} left-0 right-0 bg-n-300`
		case TagDisplayState.DISCOUNT_NOT_AVAILABLE:
			return `${baseClasses} left-0 rounded-[0_8px] bg-t-700`
		case TagDisplayState.SAVED_PERCENTAGE:
			return `${baseClasses} left-0 rounded-[0_8px] bg-r-700`
		case TagDisplayState.DISCOUNT:
			return `${baseClasses} left-0 rounded-[0_8px] bg-r-700`
		case TagDisplayState.SALE:
			return `${baseClasses} left-0 rounded-[0_8px] bg-r-700`
		default:
			return baseClasses
	}
})

const displayPrice = computed(() => {
	const SYMBOL = `≈`

	if (props.item.isBuyaholicProduct) {
		return `${SYMBOL}${props.item.localPrice}`
	}
	return getFormattedPrice(
		Number(props.item.price),
		String(props.item.currency),
	)
})

const handleClickItem = () => {
	emit('item-click', props.item, targetUrl.value)
}
</script>

<template>
	<div>
		<template v-if="!isLoading">
			<a
				:href="targetUrl"
				class="flex h-full flex-col !no-underline"
				@click.prevent="handleClickItem"
			>
				<div
					class="relative mb-12px aspect-square w-full overflow-hidden rounded-[8px]"
				>
					<div
						v-if="!isImageLoaded"
						class="h-full w-full animate-pulse bg-n-75"
					></div>
					<img
						class="h-full w-full object-cover"
						:src="item.images[0]"
						:alt="item.name"
						loading="lazy"
						@load="isImageLoaded = true"
					/>
					<div v-if="isShowTag" :class="tagClasses">
						{{ tagText }}
					</div>
				</div>

				<div class="flex flex-grow flex-col justify-between pb-8px">
					<div
						class="typography-c1-reg mb-4px line-clamp-2 text-ellipsis text-n-950"
					>
						{{ item.name }}
					</div>

					<!-- Price Section -->
					<div class="typography-s1-str text-o-500">
						{{ displayPrice }}
					</div>
				</div>
			</a>
		</template>

		<template v-else>
			<div class="flex animate-pulse flex-col gap-8px">
				<div class="aspect-square w-full rounded-[8px] bg-n-75"></div>

				<div
					v-for="i in 2"
					:key="i"
					class="h-16px w-full rounded-[4px] bg-n-75"
				></div>

				<div class="h-16px w-[44%] rounded-[4px] bg-n-75"></div>
			</div>
		</template>
	</div>
</template>
